import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';

import {ArrayOperationService} from '@services/administration/array-operation/array-operation.service';
import {LeftMenuSearchServiceService} from '@services/elastic-search/menu/left-menu-search-service.service';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {ElasticMenuItem} from '@models/elasticSearch/menu/elastic-menu-item';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {ELASTIC_SEARCH_CONSTANTS} from '@constants/elastic-search/elastic-search-constants.constant';
import {MenuItem} from '@models/sidebar/menu-item';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {Role} from '@models/user/role';
import {User} from '@models/user/user';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {ElasticSearchResult} from '@models/elastic-search/response/elastic-search-result';
import {Subject, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

@Component({
    selector: 'scitis-menu-search',
    templateUrl: './menu-search.component.html',
    styleUrls: ['./menu-search.component.scss'],
    standalone: false
})
export class MenuSearchComponent implements OnInit, OnDestroy {

  @Input()
  public isSuperAdmin: boolean = false;

  @Input()
  public userGroupId: number = -1;

  @Input()
  public firebaseUserId: string = '';

  @Input()
  public userRole: Role = new Role(new User());

  @Input()
  whiteListedMachineryGroups: number[] | null = null;

  @Output()
  searchItemAdded: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  menuItemSelected: EventEmitter<ElasticMenuItem> = new EventEmitter<ElasticMenuItem>();

  public searchInProgress = false;
  public searchResults: ElasticMenuItem[] = [];
  public searchItems: string[] = [];
  public searchResultsTotalCount: number = -1;
  public currentPageIndex: number = 0;
  public lastPageReached: boolean = false;
  private CURRENT_PAGE_NAME = 'MenuSearchComponent';
  private availableMachineIds: number[] | null = [];
  private $destroy: Subject<boolean> = new Subject<boolean>();
  private $searchRequest: Subscription|undefined;

  constructor(
      private arrayOperationService: ArrayOperationService,
      private leftMenuSearchService: LeftMenuSearchServiceService,
      private elasticSearchService: ElasticSearchService,
      private notificationService: NotificationService,
      private sortingService: SortingService,
      private translate: TranslateService) {
  }

  get displaySearchResults(): boolean {
    return this.searchItems.length > 0;
  }

  get showSearchTerms(): boolean {
    return this.searchItems.length > 0;
  }

  ngOnInit(): void {
    if (!this.isSuperAdmin) {
      this.elasticSearchService.listAllMachines(null, this.userGroupId, this.whiteListedMachineryGroups)
        .pipe(takeUntil(this.$destroy))
        .subscribe({
          next: (success: ElasticSearchResponseEntity) => {
            if (success?.result?.data && success.result.data.length > 0) {
              const result: ElasticSearchResult = success.result;
              this.availableMachineIds = [...result.data.map(i => i.id)];
            } else {
              this.availableMachineIds = [];
            }
          },
          error: (_error) => {
            this.availableMachineIds = [];
          }
        });
    } else {
      this.availableMachineIds = null;
    }
  }

  ngOnDestroy(): void {
    this.destroySearchSubscription();
    this.$destroy.next(true);
    this.$destroy.complete();
  }

  addSearchTerm(event: any): void {

    const searchTerm = event.target.value;

    if (!this.leftMenuSearchService.validateSearchTerm(searchTerm)) {
      const message = this.translate.instant('The search term %term is not valid must be at least %min characters. If an exact term search is needed use the " before and after search term.')
      .replace('%term', searchTerm).replace('%min', ELASTIC_SEARCH_CONSTANTS.MENU_ITEMS_SEARCH_TERM_MIN_SIZE);
      this.notificationService.error(message, null, this.CURRENT_PAGE_NAME, true);
      event.target.value = '';
      return;
    }

    if (this.searchItems.includes(searchTerm)) {
      event.target.value = '';
      return;
    }

    this.resetSearchProperties();
    this.searchItems.push(searchTerm);
    this.searchMenuItems();

    event.target.value = '';
    this.searchItemAdded.emit(true);
    this.currentPageIndex = 0;
  }

  removeSearchTerm(searchItem: string): void {
    this.resetSearchProperties();
    this.arrayOperationService.removeStringFromArray(searchItem, this.searchItems);
    this.searchMenuItems();
    this.currentPageIndex = 0;
  }

  onMenuItemSelected(menuItem: ElasticMenuItem): void {
    menuItem.menuItemParent = new MenuItem();
    menuItem.menuItemParent.id = menuItem.parentMenuItem.id;
    menuItem.menuItemParent.machineCategory = menuItem.parentMenuItem.machineCategory;
    menuItem.menuItemParent.organizationId = menuItem.parentMenuItem.organizationId;
    menuItem.menuItemParent.name = menuItem.parentMenuItem.name;
    if (menuItem.parentMenuItem.type === MenuItemType.NODE) {
      menuItem.nodeId = menuItem.parentMenuItem.id; // ONLY WORKING IF THIS IS REALLY THE NODE ID AND NOT THE MACHINE ID
    }
    if (menuItem.parentMenuItem.type === MenuItemType.NODE) {
      menuItem.menuItemParent.type = MenuItemType.MACHINE;
    } else {
      menuItem.menuItemParent.type = menuItem.parentMenuItem.type;
    }
    this.menuItemSelected.emit(menuItem);
    this.resetSearch();
  }

  resetSearch(): void {
    this.currentPageIndex = 0;
    this.searchItems = [];
  }

  public onLoadMoreItems() {
    if ((this.currentPageIndex + 1) * ELASTIC_SEARCH_CONSTANTS.MENU_ITEMS_SEARCH_RESULTS_SIZE < this.searchResultsTotalCount) {
      this.currentPageIndex++;
      this.searchMenuItems(true);
    }
  }

  private resetSearchProperties() {
    this.currentPageIndex = 0;
    this.searchResultsTotalCount = -1;
    this.searchResults = [];
  }

  private searchMenuItems(loadMore: boolean = false) {
    if (this.searchItems.length === 0) {
      return;
    }

    this.destroySearchSubscription();

    this.searchInProgress = true;
    const from: number = this.currentPageIndex * ELASTIC_SEARCH_CONSTANTS.MENU_ITEMS_SEARCH_RESULTS_SIZE;
    this.$searchRequest = this.leftMenuSearchService.searchMenuItems(this.searchItems, this.firebaseUserId, this.userGroupId, this.userRole, from, this.whiteListedMachineryGroups, this.availableMachineIds)
      .pipe(takeUntil(this.$destroy))
      .subscribe({
        next: (result: ElasticSearchResponseEntity[]) => {
          const pageResults = [...result[0].result.data, ...result[1].result.data, ...result[2].result.data];
          this.searchResults = loadMore
            ? [...this.searchResults, ...this.sortingService.sortArrayByProperty(pageResults, 'name', true)]
            : this.sortingService.sortArrayByProperty(pageResults, 'name', true);
          if (this.searchResultsTotalCount < 0) {
            this.searchResultsTotalCount = result[0].result.totalHits + result[1].result.totalHits + result[2].result.totalHits;
          }
          this.searchInProgress = false;

        },
        error: (error) => {
          const message = this.translate.instant('There was an error searching menu items by ') + this.searchItems.join(',');
          this.notificationService.error(message, error, this.CURRENT_PAGE_NAME, true);
          this.searchInProgress = false;
        }
      });
  }

  destroySearchSubscription() {
    if (this.$searchRequest) {
      this.$searchRequest.unsubscribe();
    }
  }
}
